import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Edit2, PackageSearch, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const NONE = '__none__';
const stockTypeLabels = { FIXED: 'Fixo', ITINERANT: 'Itinerante' };
const movementLabels = { IN: 'Entrada', OUT: 'Saída', ADJUSTMENT: 'Ajuste', TRANSFER: 'Transferência' };

const emptyStockForm = {
  code: '',
  name: '',
  type: 'FIXED',
  itinerantType: '',
  postalCode: '',
  address: '',
  responsibleId: NONE,
  pdvLocationId: NONE,
};

const emptyMovementForm = {
  productId: '',
  fromStockId: NONE,
  toStockId: NONE,
  type: 'IN',
  quantity: '',
  note: '',
};

const StockPage = () => {
  const { user, isAdmin } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [pdvLocations, setPdvLocations] = useState([]);
  const [search, setSearch] = useState('');
  const [stockDialog, setStockDialog] = useState(false);
  const [movementDialog, setMovementDialog] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockForm, setStockForm] = useState(emptyStockForm);
  const [movementForm, setMovementForm] = useState(emptyMovementForm);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadData = async () => {
    try {
      const [stocksResponse, itemsResponse, movementsResponse, productsResponse, pdvResponse] = await Promise.all([
        api.getStocks({ page: 1, limit: 100, search }),
        api.getStockItems({ page: 1, limit: 100, search }),
        api.getStockMovements({ page: 1, limit: 100, search }),
        api.getProducts({ page: 1, limit: 100 }),
        api.getPdvLocations({ page: 1, limit: 100 }),
      ]);
      setStocks(stocksResponse.data || []);
      setItems(itemsResponse.data || []);
      setMovements(movementsResponse.data || []);
      setProducts(productsResponse.data || []);
      setPdvLocations(pdvResponse.data || []);

      if (isAdmin()) {
        const usersResponse = await api.getUsers({ page: 1, limit: 100 });
        setUsers(usersResponse.data || []);
      } else if (user) {
        setUsers([user]);
      }
    } catch (error) {
      toast.error('Erro ao carregar estoque');
    }
  };

  const openStockDialog = (stock = null) => {
    setEditingStock(stock);
    setStockForm(stock ? {
      code: stock.code,
      name: stock.name,
      type: stock.type || 'FIXED',
      itinerantType: stock.itinerantType || '',
      postalCode: '',
      address: stock.address || '',
      responsibleId: stock.responsibleId || NONE,
      pdvLocationId: stock.pdvLocationId || NONE,
    } : emptyStockForm);
    setStockDialog(true);
  };

  const lookupPostalCode = async () => {
    const postalCode = stockForm.postalCode.replace(/\D/g, '');
    if (postalCode.length !== 8) {
      toast.error('Informe um CEP com 8 dígitos');
      return;
    }

    try {
      const result = await api.lookupPostalCode(postalCode);
      if (result.addressLine) {
        setStockForm((current) => ({ ...current, address: result.addressLine }));
        toast.success('Endereço preenchido pelo CEP');
      } else {
        toast.info('CEP encontrado, mas sem endereço completo');
      }
    } catch (error) {
      toast.error('Não foi possível consultar o CEP. Preencha o endereço manualmente.');
    }
  };

  const saveStock = async (event) => {
    event.preventDefault();
    const stockData = { ...stockForm };
    delete stockData.postalCode;
    const payload = {
      ...stockData,
      itinerantType: stockForm.type === 'ITINERANT' ? stockForm.itinerantType || null : null,
      address: stockForm.address || null,
      responsibleId: stockForm.responsibleId === NONE ? null : stockForm.responsibleId,
      pdvLocationId: stockForm.type === 'FIXED' && stockForm.pdvLocationId !== NONE ? stockForm.pdvLocationId : null,
    };
    try {
      if (editingStock) {
        await api.updateStock(editingStock.id, payload);
        toast.success('Estoque atualizado');
      } else {
        await api.createStock(payload);
        toast.success('Estoque criado');
      }
      setStockDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar estoque');
    }
  };

  const saveMovement = async (event) => {
    event.preventDefault();
    const isTransfer = movementForm.type === 'TRANSFER';
    const stockId = movementForm.fromStockId !== NONE ? movementForm.fromStockId : movementForm.toStockId;
    const movementPayload = {
      productId: movementForm.productId,
      type: movementForm.type,
      quantity: Number(movementForm.quantity),
      note: movementForm.note || null,
      fromStockId: null,
      toStockId: null,
    };

    if (isTransfer) {
      movementPayload.fromStockId = movementForm.fromStockId === NONE ? null : movementForm.fromStockId;
      movementPayload.toStockId = movementForm.toStockId === NONE ? null : movementForm.toStockId;
    } else if (movementForm.type === 'OUT') {
      movementPayload.fromStockId = stockId === NONE ? null : stockId;
    } else {
      movementPayload.toStockId = stockId === NONE ? null : stockId;
    }

    try {
      await api.createStockMovement(movementPayload);
      toast.success('Movimentação registrada');
      setMovementDialog(false);
      setMovementForm(emptyMovementForm);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao movimentar estoque');
    }
  };

  const removeStock = async (id) => {
    if (!window.confirm('Remover este estoque?')) return;
    try {
      await api.deleteStock(id);
      toast.success('Estoque removido');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover estoque');
    }
  };

  const stockLabel = (stock) => {
    if (!stock) return '-';
    const type = stock.type === 'ITINERANT' && stock.itinerantType
      ? stock.itinerantType
      : stockTypeLabels[stock.type] || stock.type;
    return `${type} ${stock.code} - ${stock.name}`;
  };

  const setMovementType = (type) => {
    setMovementForm((current) => ({
      ...current,
      type,
      fromStockId: NONE,
      toStockId: NONE,
    }));
  };

  const setSingleMovementStock = (stockId) => {
    setMovementForm((current) => ({
      ...current,
      fromStockId: current.type === 'OUT' ? stockId : NONE,
      toStockId: current.type === 'OUT' ? NONE : stockId,
    }));
  };

  const singleMovementStockId = movementForm.type === 'OUT'
    ? movementForm.fromStockId
    : movementForm.toStockId;

  const renderStockDialog = () => (
    <Dialog open={stockDialog} onOpenChange={setStockDialog}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={() => openStockDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Estoque
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editingStock ? 'Editar Estoque' : 'Novo Estoque'}</DialogTitle></DialogHeader>
        <form onSubmit={saveStock} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Código</Label>
              <Input value={stockForm.code} onChange={(e) => setStockForm({ ...stockForm, code: e.target.value.toUpperCase() })} required />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={stockForm.type} onValueChange={(value) => setStockForm({ ...stockForm, type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIXED">Fixo</SelectItem>
                  <SelectItem value="ITINERANT">Itinerante</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input value={stockForm.name} onChange={(e) => setStockForm({ ...stockForm, name: e.target.value })} required />
          </div>
          {stockForm.type === 'ITINERANT' && (
            <div className="space-y-2">
              <Label>Tipo itinerante</Label>
              <Input value={stockForm.itinerantType} onChange={(e) => setStockForm({ ...stockForm, itinerantType: e.target.value })} placeholder="Veículo, Stand, etc." />
            </div>
          )}
          <div className="space-y-2">
            <Label>CEP</Label>
            <div className="flex gap-2">
              <Input
                value={stockForm.postalCode}
                onChange={(e) => setStockForm({ ...stockForm, postalCode: e.target.value })}
                placeholder="00000000"
                inputMode="numeric"
                maxLength={9}
              />
              <Button type="button" variant="outline" onClick={lookupPostalCode}>
                Buscar
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Endereço</Label>
            <Input value={stockForm.address} onChange={(e) => setStockForm({ ...stockForm, address: e.target.value })} />
          </div>
          {stockForm.type === 'FIXED' && (
            <div className="space-y-2">
              <Label>PDV vinculado</Label>
              <Select value={stockForm.pdvLocationId} onValueChange={(value) => setStockForm({ ...stockForm, pdvLocationId: value })}>
                <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Sem PDV</SelectItem>
                  {pdvLocations.map((item) => <SelectItem key={item.id} value={item.id}>{item.code} - {item.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Responsável</Label>
            <Select value={stockForm.responsibleId} onValueChange={(value) => setStockForm({ ...stockForm, responsibleId: value })}>
              <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Sem responsável</SelectItem>
                {users.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full">{editingStock ? 'Atualizar' : 'Cadastrar'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderMovementDialog = () => (
    <Dialog open={movementDialog} onOpenChange={setMovementDialog}>
      <DialogTrigger asChild>
        <Button onClick={() => setMovementDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Movimento
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nova Movimentação</DialogTitle></DialogHeader>
        <form onSubmit={saveMovement} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={movementForm.type} onValueChange={setMovementType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="IN">Entrada</SelectItem>
                <SelectItem value="OUT">Saída</SelectItem>
                <SelectItem value="ADJUSTMENT">Ajuste</SelectItem>
                <SelectItem value="TRANSFER">Transferência</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {movementForm.type === 'TRANSFER' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Origem</Label>
                <Select value={movementForm.fromStockId} onValueChange={(value) => setMovementForm({ ...movementForm, fromStockId: value })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem origem</SelectItem>
                    {stocks.map((item) => <SelectItem key={item.id} value={item.id}>{item.code} - {item.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destino</Label>
                <Select value={movementForm.toStockId} onValueChange={(value) => setMovementForm({ ...movementForm, toStockId: value })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem destino</SelectItem>
                    {stocks.map((item) => <SelectItem key={item.id} value={item.id}>{item.code} - {item.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Stock</Label>
              <Select value={singleMovementStockId} onValueChange={setSingleMovementStock}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Sem stock</SelectItem>
                  {stocks.map((item) => <SelectItem key={item.id} value={item.id}>{item.code} - {item.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Produto</Label>
            <Select value={movementForm.productId} onValueChange={(value) => setMovementForm({ ...movementForm, productId: value })}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{products.map((item) => <SelectItem key={item.id} value={item.id}>{item.code} - {item.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Quantidade</Label>
            <Input type="number" min="1" value={movementForm.quantity} onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Observação</Label>
            <Textarea value={movementForm.note} onChange={(e) => setMovementForm({ ...movementForm, note: e.target.value })} />
          </div>
          <Button type="submit" className="w-full">Registrar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Estoque</h1>
          <p className="text-muted-foreground mt-1">Saldos, movimentações e cadastros de estoque</p>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            {renderStockDialog()}
            {renderMovementDialog()}
          </div>
        )}
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Pesquisar por estoque, produto ou observação..." searchFields={['Estoque', 'Produto', 'Observação']} />

      <Tabs defaultValue="saldo">
        <TabsList>
          <TabsTrigger value="saldo">Saldo</TabsTrigger>
          <TabsTrigger value="movimentacoes">Movimentações</TabsTrigger>
          <TabsTrigger value="estoque">Estoque</TabsTrigger>
        </TabsList>
        <TabsContent value="saldo">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-6 py-3 text-left text-sm font-semibold">Estoque</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Produto</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold">Quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-muted/50">
                      <td className="px-6 py-4 text-sm">{stockLabel(item.stock || item.location)}</td>
                      <td className="px-6 py-4 text-sm">{item.product?.code} - {item.product?.name}</td>
                      <td className="px-6 py-4 text-sm text-center"><Badge>{item.quantity}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="movimentacoes">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-6 py-3 text-left text-sm font-semibold">Data</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Tipo</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Produto</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Origem</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Destino</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold">Qtd.</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-muted/50">
                      <td className="px-6 py-4 text-sm">{new Date(item.createdAt).toLocaleString('pt-BR')}</td>
                      <td className="px-6 py-4 text-sm">{movementLabels[item.type]}</td>
                      <td className="px-6 py-4 text-sm">{item.product?.code} - {item.product?.name}</td>
                      <td className="px-6 py-4 text-sm">{stockLabel(item.fromStock || item.fromLocation)}</td>
                      <td className="px-6 py-4 text-sm">{stockLabel(item.toStock || item.toLocation)}</td>
                      <td className="px-6 py-4 text-sm text-center"><Badge>{item.quantity}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="estoque">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-6 py-3 text-left text-sm font-semibold">Código</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Tipo</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Detalhe</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">PDV</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Responsável</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map((stock) => (
                    <tr key={stock.id} className="border-b hover:bg-muted/50">
                      <td className="px-6 py-4 text-sm font-mono">{stock.code}</td>
                      <td className="px-6 py-4 text-sm font-medium">{stock.name}</td>
                      <td className="px-6 py-4 text-sm">{stockTypeLabels[stock.type] || stock.type}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{stock.type === 'ITINERANT' ? stock.itinerantType || '-' : stock.address || '-'}</td>
                      <td className="px-6 py-4 text-sm">{stock.pdvLocation ? `${stock.pdvLocation.code} - ${stock.pdvLocation.name}` : '-'}</td>
                      <td className="px-6 py-4 text-sm">{stock.responsible?.name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-center">
                        {isAdmin() ? <Switch checked={stock.enabled} onCheckedChange={() => api.toggleStock(stock.id).then(loadData)} /> : <Badge>{stock.enabled ? 'Ativo' : 'Inativo'}</Badge>}
                      </td>
                      <td className="px-6 py-4 text-sm text-right space-x-2">
                        {isAdmin() && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openStockDialog(stock)}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => removeStock(stock.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {stocks.length === 0 && items.length === 0 && movements.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <PackageSearch className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum estoque encontrado.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StockPage;
